import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stepper, Step, StepLabel, Chip, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import AgentCommandBar from './AgentCommandBar';
import BreakdownPreview from './BreakdownPreview';
import TicketList from './TicketList';
import { fetchBreakdownStatus } from '../../store/strategistSlice';
import './StrategistAgent.scss';

const BREAKDOWN_STEPS = {
  REQUIREMENT: 0,
  PREVIEW: 1,
  TICKET_GENERATION: 2,
};

const BreakdownStepper = ({
  projectId,
  onCreateAC,
  resetTrigger,
  refreshTrigger,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [expandedSteps, setExpandedSteps] = useState(new Set([0])); // Allow multiple steps to be open
  const [breakdownData, setBreakdownData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [lastProjectId, setLastProjectId] = useState(null);
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState(0);
  const commandBarRef = React.useRef();

  const clearBreakdownData = () => {
    setBreakdownData(null);
    setTickets([]);
    setExpandedSteps(new Set([0]));
    // Clear the command bar fields
    if (commandBarRef.current) {
      commandBarRef.current.clearFields();
    }
  };

  const fetchBreakdownData = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await dispatch(fetchBreakdownStatus(projectId)).unwrap();
      if (response.data && Object.keys(response.data).length > 0) {
        // Filter agent_output to keep only first item with full nested structure
        const filteredData = { ...response.data };
        if (filteredData.agent_output) {
          const agentOutput = filteredData.agent_output;
          
          // If there are error fields, keep the original agent_output
          if (agentOutput.error || agentOutput.parseError) {
            filteredData.agent_output = agentOutput;
          } else {
            const filtered = {};
            
            // Priority order: epic > feature > task > subtask
            // Keep the first available item with its full nested structure
            if (agentOutput.epic) {
              filtered.epic = agentOutput.epic;
            } else if (agentOutput.epics && agentOutput.epics.length > 0) {
              filtered.epic = agentOutput.epics[0];
            } else if (agentOutput.features && agentOutput.features.length > 0) {
              filtered.feature = agentOutput.features[0];
            } else if (agentOutput.tasks && agentOutput.tasks.length > 0) {
              filtered.task = agentOutput.tasks[0];
            } else if (agentOutput.subtasks && agentOutput.subtasks.length > 0) {
              filtered.subtask = agentOutput.subtasks[0];
            }
            
            filteredData.agent_output = filtered;
          }
        }
        
        setBreakdownData(filteredData);
        if (response.data.generated_tickets) {
          // Convert hierarchical tickets to format expected by TicketList
          const convertToHierarchical = (ticketNode) => {
            const ticket = {
              id: ticketNode.id,
              title: ticketNode.title,
              ticketNumber: ticketNode.ticket_number,
              type: ticketNode.task_type,
              priority: ticketNode.priority,
              children: []
            };

            // Add features as children
            if (ticketNode.features) {
              ticketNode.features.forEach(feature => {
                const featureTicket = convertToHierarchical(feature);
                ticket.children.push(featureTicket);
              });
            }

            // Add tasks as children
            if (ticketNode.tasks) {
              ticketNode.tasks.forEach(task => {
                const taskTicket = convertToHierarchical(task);
                ticket.children.push(taskTicket);
              });
            }

            // Add subtasks as children
            if (ticketNode.subtasks) {
              ticketNode.subtasks.forEach(subtask => {
                const subtaskTicket = convertToHierarchical(subtask);
                ticket.children.push(subtaskTicket);
              });
            }

            return ticket;
          };

          const hierarchicalTickets = [];
          if (response.data.generated_tickets.epic || response.data.generated_tickets.task) {
            hierarchicalTickets.push(convertToHierarchical(response.data.generated_tickets.epic || response.data.generated_tickets.task));
          }
          setTickets(hierarchicalTickets);
        } else {
          setTickets([]);
        }
      } else {
        // Clear data when response is empty
        clearBreakdownData();
      }
    } catch (error) {
      setBreakdownData({ status: 'FAILED', agent_output: { error: error.message || 'Failed to fetch breakdown data' } });
    }
  }, [projectId, dispatch]);

  // Handle reset trigger
  useEffect(() => {
    if (resetTrigger > 0) {
      clearBreakdownData();
    }
  }, [resetTrigger]);

  // Handle refresh trigger - only if it's a new refresh and not caused by project change
  useEffect(() => {
    if (refreshTrigger > 0 && refreshTrigger !== lastRefreshTrigger && projectId) {
      setLastRefreshTrigger(refreshTrigger);
      fetchBreakdownData();
    }
  }, [refreshTrigger, projectId, lastRefreshTrigger, fetchBreakdownData]);

  useEffect(() => {
    if (projectId && projectId !== lastProjectId) {
      clearBreakdownData();
      setLastProjectId(projectId);
      fetchBreakdownData();
    }
  }, [projectId, lastProjectId, fetchBreakdownData]);

  // Auto-expand based on current stage - only set initial state
  useEffect(() => {
    if (breakdownData) {
      const stage = breakdownData.current_stage;
      let initialStep = 0;
      if (stage === 3) {
        initialStep = 2; // Show tickets
      } else if (stage === 2) {
        initialStep = 1; // Show breakdown preview
      } else if (stage === 1) {
        initialStep = 0; // Show requirement (generating)
      }
      setExpandedSteps(new Set([initialStep]));
    }
  }, [breakdownData]);

  const steps = [
    { label: t('agent.stepper.requirement'), key: BREAKDOWN_STEPS.REQUIREMENT },
    { label: t('agent.stepper.preview'), key: BREAKDOWN_STEPS.PREVIEW },
    { label: t('agent.stepper.ticketGeneration'), key: BREAKDOWN_STEPS.TICKET_GENERATION },
  ];

  const getActiveStep = () => {
    if (!breakdownData) return 0;
    const stage = breakdownData.current_stage;
    if (stage >= 3) return 2;
    if (stage >= 2) return 1;
    return 0;
  };

  const getStepStatus = (stepIndex) => {
    if (!breakdownData) return 'PENDING';
    const stage = breakdownData.current_stage;

    if (stepIndex === 0) {
      return stage >= 1 ? 'COMPLETED' : 'PENDING';
    }
    if (stepIndex === 1) {
      if (stage >= 2) return stage > 2 ? 'COMPLETED' : 'IN_PROGRESS';
      return 'PENDING';
    }
    if (stepIndex === 2) {
      return stage >= 3 ? 'COMPLETED' : 'PENDING';
    }
    return 'PENDING';
  };

  const getStepStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return '#16a34a';
      case 'IN_PROGRESS':
        return '#3b82f6';
      case 'PENDING':
      default:
        return '#6b7280';
    }
  };

  const handleStepClick = (stepIndex) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stepIndex)) {
        newSet.delete(stepIndex);
      } else {
        newSet.add(stepIndex);
      }
      return newSet;
    });
  };

  return (
    <Box className="breakdown-stepper-container">
      <Stepper activeStep={getActiveStep()} orientation="vertical" className="breakdown-stepper">
        {/* Step 1: Requirement */}
        <Step completed={getActiveStep() > 0}>
          <StepLabel onClick={() => handleStepClick(0)} data-expanded={expandedSteps.has(0)}>
            <Box className="step-header">
              <Box className="step-title">
                {steps[0].label}
              </Box>
              <Chip
                label={getStepStatus(0)}
                size="small"
                className="step-status"
                sx={{ bgcolor: getStepStatusColor(getStepStatus(0)), color: 'white' }}
              />
            </Box>
          </StepLabel>
          {expandedSteps.has(0) && (
            <Box className="step-content">
              <AgentCommandBar
                projectId={projectId}
                initialRequirement={breakdownData?.requirement}
                outputLevel={breakdownData?.customHierarchy}
                isGenerating={breakdownData?.current_stage === 1}
                onBreakdownCreated={fetchBreakdownData}
                onClearRequested={commandBarRef}
              />
            </Box>
          )}
        </Step>

        {/* Step 2: Preview */}
        <Step completed={getActiveStep() > 1}>
          <StepLabel onClick={() => handleStepClick(1)} data-expanded={expandedSteps.has(1)}>
            <Box className="step-header">
              <Box className="step-title">
                {steps[1].label}
              </Box>
              <Chip
                label={getStepStatus(1)}
                size="small"
                className="step-status"
                sx={{ bgcolor: getStepStatusColor(getStepStatus(1)), color: 'white' }}
              />
            </Box>
          </StepLabel>
          {expandedSteps.has(1) && (
            <Box className="step-content">
              {breakdownData?.status === 'FAILED' || (breakdownData?.agent_output && (breakdownData.agent_output.error || breakdownData.agent_output.parseError)) ? (
                <Paper elevation={2} className="breakdown-preview-paper">
                  <Alert severity="error" className="breakdown-preview-error-alert" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      {t('agent.breakdown.agentIssue')}
                      {(breakdownData?.agent_output?.error || breakdownData?.agent_output?.parseError) && (
                        <> : {breakdownData?.agent_output?.error || breakdownData?.agent_output?.parseError}</>
                      )}
                      <br />
                      {t('agent.breakdown.pleaseTryAgain')}
                    </Box>
                  </Alert>
                </Paper>
              ) : breakdownData?.status === 'SUCCESS' && breakdownData?.current_stage === 2 && !breakdownData?.agent_output ? (
                <Paper elevation={2} className="breakdown-preview-paper">
                  <Typography variant="body1" color="text.secondary" align="center">
                    {t('agent.history.noBreakdownGenerated')}
                  </Typography>
                </Paper>
              ) : breakdownData?.current_stage >= 1 && !breakdownData?.agent_output ? (
                <Box className="ac-generating-container">
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={32} sx={{ mb: 2 }} />
                    <Typography variant="body1">
                      {t('agent.breakdown.generatingBreakdown')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {t('agent.breakdown.refreshToSeeUpdates')}
                    </Typography>
                  </Box>
                </Box>
              ) : breakdownData?.agent_output ? (
                <BreakdownPreview
                  breakdown={breakdownData.agent_output}
                  insights={breakdownData.insights}
                  projectId={projectId}
                  outputLevel={breakdownData.customHierarchy}
                  ticketsGenerated={breakdownData.generated_tickets ? true : false}
                  agentLogId={breakdownData.agent_log_id}
                  breakdownHistoryId={breakdownData.breakdown_history_id}
                  onRefresh={fetchBreakdownData}
                />
              ) : (
                <Box className="step-placeholder">
                  <Typography variant="body2" color="text.secondary">
                    {t('agent.stepper.messages.completeRequirement')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Step>

        {/* Step 3: Ticket Generation */}
        <Step completed={getActiveStep() > 2}>
          <StepLabel onClick={() => handleStepClick(2)} data-expanded={expandedSteps.has(2)}>
            <Box className="step-header">
              <Box className="step-title">
                {steps[2].label}
              </Box>
              <Chip
                label={getStepStatus(2)}
                size="small"
                className="step-status"
                sx={{ bgcolor: getStepStatusColor(getStepStatus(2)), color: 'white' }}
              />
            </Box>
          </StepLabel>
          {expandedSteps.has(2) && (
            <Box className="step-content">
              {tickets && tickets.length > 0 ? (
                <TicketList tickets={tickets} onCreateAC={onCreateAC} />
              ) : (
                <Box className="step-placeholder">
                  <Typography variant="body2" color="text.secondary">
                    {t('agent.stepper.messages.completePreview')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Step>
      </Stepper>
    </Box>
  );
};

export default BreakdownStepper;
export { BREAKDOWN_STEPS };

